import { useEffect, useMemo, useRef, useState } from 'react'
import posthog from 'posthog-js'
import { useQuery } from 'react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import InfiniteScroll from 'react-infinite-scroll-component'
import {
    IoClose,
    IoInformationCircleOutline,
    IoReceiptOutline,
    IoSearch,
} from 'react-icons/io5'
import { getBoxes } from 'services/funds'
import Header from 'components/headers/funds'
import SideBar from 'components/sidebar'
import SideBarItems from 'components/sidebar/items'
import DotsLoading from 'components/loadings/dotsLoading'
import CustomBottomSheet from 'components/bottomSheet'
import SortPill from 'components/sortPill'
import { SectionError } from 'components/states'
import { describeDay } from 'modules/fund/tabs/transactions/days'
import useGetTransactions from './hooks/useGetTransactions'
import useMemberId from './hooks/useMemberId'
import {
    activeFilters,
    boxTitleFromRows,
    commissionOf,
    dayRangeLabel,
    describeTransaction,
    filterAnalytics,
    filterParams,
    fundOptions,
    hidesTopUps,
    ITransactionFilters,
    linkWalletRows,
    NO_FILTERS,
    PERIOD_OPTIONS,
    periodParam,
    periodRange,
    periodRangeLabel,
    periodTotals,
    readFilters,
    runsByDay,
    sameFilters,
    TFilterName,
    TPeriod,
    writeFilters,
} from './helpers'
import Item from './item'
import TransactionDetails from './details'
import PeriodSummary from './summary'
import { FilterButton, FilterChips, filterChips, FilterSheet } from './filters'

// The fund tab's pause: long enough that a name being typed is one request,
// short enough that the list answers before anyone wonders whether it heard.
const SEARCH_DEBOUNCE_MS = 450

// How far a period's summary reads ahead on its own: ten pages, three hundred
// payments — a year of a busy member's boxes. Past it the summary says what it
// was built from instead of reading on forever.
const SUMMARY_MAX_PAGES = 10

const PERIOD_KEYS = PERIOD_OPTIONS.map((option) => option.key)

/** The URL's `period`, or «همه» for anything it does not know. */
const readPeriod = (params: URLSearchParams): TPeriod => {
    const value = params.get('period') as TPeriod
    return value && PERIOD_KEYS.includes(value) ? value : ''
}

/** One string per set of filters, to tell one answer from the next. */
const filtersKey = (filters: ITransactionFilters) =>
    writeFilters(new URLSearchParams(), filters).toString()

type TClearSource = 'chip' | 'sheet' | 'empty_state'

function ListSkeleton() {
    return (
        <div role="status" aria-live="polite">
            <span className="sr-only">در حال دریافت تراکنش‌ها</span>
            <div aria-hidden className="animate-pulse">
                {[4, 2].map((count, day) => (
                    <div key={day}>
                        <div className="mx-5 mb-2.5 mt-5 h-3 w-28 rounded bg-divider" />
                        <div className="mx-4 divide-y divide-divider overflow-hidden rounded-card border border-[#E4E7EB] bg-white">
                            {Array.from({ length: count }, (_, row) => (
                                <div
                                    key={row}
                                    className="flex items-center gap-2.5 px-3 py-3.5"
                                >
                                    <div className="h-9 w-9 shrink-0 rounded-[10px] bg-divider" />
                                    <div className="min-w-0 flex-1">
                                        <div className="h-3.5 w-32 rounded bg-divider" />
                                        <div className="mt-2 h-3 w-20 rounded bg-divider" />
                                    </div>
                                    <div className="h-3.5 w-20 rounded bg-divider" />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default function TransactionsPage() {
    const navigate = useNavigate()
    const { id: me, isLoading: isResolvingMe } = useMemberId()

    // The search, the period and the filters live in the URL, so leaving the
    // list for a payment's box and coming back — or reloading — finds it the
    // way it was left. Replaced rather than pushed, as the inbox does: «back»
    // leaves the list instead of stepping through every filter tried.
    const [params, setParams] = useSearchParams()
    const paramsRef = useRef(params)
    paramsRef.current = params
    const updateParams = (
        change: (draft: URLSearchParams) => URLSearchParams | void
    ) => {
        const draft = new URLSearchParams(paramsRef.current)
        const next = change(draft) ?? draft
        if (next.toString() === paramsRef.current.toString()) return
        paramsRef.current = next
        setParams(next, { replace: true })
    }

    const filters = useMemo(() => readFilters(params), [params])
    const hasDays = !!filters.from || !!filters.to
    // A range of days takes the place of a period, never narrows it.
    const period: TPeriod = hasDays ? '' : readPeriod(params)
    const appliedSearch = (params.get('q') ?? '').trim()

    const [searchText, setSearchText] = useState(appliedSearch)
    const [openPk, setOpenPk] = useState<number | null>(null)
    const [filterOpen, setFilterOpen] = useState(false)

    const date = useMemo(() => periodParam(period), [period])
    const range = useMemo(() => periodRange(period), [period])
    const serverFilters = useMemo(() => filterParams(filters), [filters])
    const filterNames = activeFilters(filters)
    const topUpsHidden = hidesTopUps(filters)

    const {
        data,
        isLoading,
        isError,
        refetch,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useGetTransactions({ q: appliedSearch, date, ...serverFilters })

    // The member's boxes, for the sheet's list and the name on a fund's
    // chip. Only asked for once either is on screen.
    const boxes = useQuery(['get-boxes'], getBoxes, {
        staleTime: 60_000,
        enabled: filterOpen || !!filters.box,
    })
    const funds = useMemo(() => fundOptions(boxes.data), [boxes.data])

    const applySearch = (text: string) => {
        const q = text.trim()
        updateParams((next) => {
            if (q) next.set('q', q)
            else next.delete('q')
        })
    }

    useEffect(() => {
        posthog.capture('my_transactions_viewed')
    }, [])

    useEffect(() => {
        const handle = setTimeout(
            () => applySearch(searchText),
            SEARCH_DEBOUNCE_MS
        )
        return () => clearTimeout(handle)
        // `applySearch` reads the URL through a ref, so it is never stale.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchText])

    // Pages are fetched at different moments, so a payment made while the
    // list is open pushes a row from the end of one page onto the start of
    // the next. Seen twice, it is shown once.
    const rows = useMemo(() => {
        if (!data) return undefined
        const seen = new Set<number>()
        return data.pages
            .flatMap((page) => page?.results ?? [])
            .filter((row) => {
                if (seen.has(row.pk)) return false
                seen.add(row.pk)
                return true
            })
    }, [data])

    const days = useMemo(() => (rows ? runsByDay(rows) : []), [rows])

    // A period or a range of days: a window whose rows add up to something.
    const hasWindow = period !== '' || hasDays

    const pageCount = data?.pages.length ?? 0
    const isCounting =
        hasWindow &&
        !!hasNextPage &&
        pageCount < SUMMARY_MAX_PAGES &&
        !isError
    const summaryCapped =
        hasWindow && !!hasNextPage && pageCount >= SUMMARY_MAX_PAGES

    // A period's summary is only true once every row of it is in, so it reads
    // ahead on its own rather than waiting for a scroll to the bottom.
    useEffect(() => {
        if (!isCounting || isFetchingNextPage) return
        fetchNextPage()
    }, [isCounting, isFetchingNextPage, pageCount, fetchNextPage])

    // Which rows are the two halves of one gateway payment — a top-up and
    // the payment it was handed straight on to. Worked out over the whole
    // list, so a pair split across two pages is still seen once both are in.
    const links = useMemo(
        () => (rows ? linkWalletRows(rows, me) : null),
        [rows, me]
    )

    const totals = useMemo(
        () =>
            rows && hasWindow
                ? periodTotals(rows, me, links ?? undefined)
                : null,
        [rows, hasWindow, me, links]
    )

    // How many of the period's top-ups were spent on the spot, for the note
    // under the summary that says they are not counted twice.
    const passThroughCount = useMemo(() => {
        if (!links) return 0
        let count = 0
        links.forEach((link) => {
            if (link.spentOn) count += 1
        })
        return count
    }, [links])

    // Read from the live list, not remembered at tap time: the sheet stays
    // open across a refetch.
    const openItem = useMemo(
        () =>
            openPk !== null && rows
                ? rows.find((row) => row.pk === openPk)
                : undefined,
        [openPk, rows]
    )

    // What a search found. The text never leaves the device — only how long
    // it was and whether it matched anything.
    const firstPageLength = data?.pages?.[0]?.results?.length
    useEffect(() => {
        if (appliedSearch === '' || firstPageLength === undefined) return
        posthog.capture('my_transactions_searched', {
            query_length: appliedSearch.length,
            result_count: firstPageLength,
            is_empty: firstPageLength === 0,
            period: period || null,
        })
        // Keyed on the search and the size of its answer, not on the page
        // identity, which changes on every background refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedSearch, firstPageLength])

    useEffect(() => {
        if (!isError) return
        posthog.capture('my_transactions_load_failed', {
            has_search: appliedSearch !== '',
            period: period || null,
            has_rows: !!rows?.length,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError])

    // `transactions_filter_apply` goes out once the list it asked for has
    // come back, so it can say how many rows that was.
    const pendingApply = useRef<{
        key: string
        props: Record<string, unknown>
    } | null>(null)
    const currentKey = filtersKey(filters)
    // What the first page of the list on screen came to. The first page
    // only: the rest is read as the member scrolls, or never.
    const resultProps = () => {
        const failed = firstPageLength === undefined
        return {
            result_count: failed ? null : firstPageLength,
            has_more: failed ? null : !!hasNextPage,
            is_empty: failed ? null : firstPageLength === 0,
            failed,
        }
    }
    useEffect(() => {
        const pending = pendingApply.current
        if (!pending || pending.key !== currentKey) return
        if (firstPageLength === undefined && !isError) return
        pendingApply.current = null
        posthog.capture('transactions_filter_apply', {
            ...pending.props,
            ...resultProps(),
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentKey, firstPageLength, isError])

    const setFilters = (next: ITransactionFilters) =>
        updateParams((current) => writeFilters(current, next))

    const clearFilters = (
        which: TFilterName | 'all',
        source: TClearSource
    ) => {
        posthog.capture('transactions_filter_clear', {
            filter: which,
            source,
            filter_count_before: filterNames.length,
            has_search: appliedSearch !== '',
        })
        setFilters(
            which === 'all'
                ? NO_FILTERS
                : which === 'box'
                ? { ...filters, box: null }
                : which === 'kind'
                ? { ...filters, kind: null }
                : { ...filters, from: null, to: null }
        )
    }

    const openFilters = () => {
        posthog.capture('transactions_filter_open', {
            active_filter_count: filterNames.length,
            has_box: !!filters.box,
            has_kind: !!filters.kind,
            has_date: hasDays,
            has_search: appliedSearch !== '',
            period: period || null,
        })
        setFilterOpen(true)
    }

    const applyFilters = (next: ITransactionFilters) => {
        setFilterOpen(false)
        // Nothing chosen over something that was: that is a clear.
        if (activeFilters(next).length === 0 && filterNames.length > 0) {
            clearFilters('all', 'sheet')
            return
        }
        const changed = !sameFilters(next, filters)
        const props = {
            ...filterAnalytics(next),
            changed,
            has_search: appliedSearch !== '',
            period: next.from || next.to ? null : period || null,
        }
        if (!changed) {
            posthog.capture('transactions_filter_apply', {
                ...props,
                ...resultProps(),
            })
            return
        }
        pendingApply.current = { key: filtersKey(next), props }
        setFilters(next)
    }

    const choosePeriod = (next: TPeriod) => {
        if (next === period && !hasDays) return
        posthog.capture('my_transactions_period_filtered', {
            period: next || null,
            has_search: appliedSearch !== '',
        })
        updateParams((params) => {
            if (next) params.set('period', next)
            else params.delete('period')
            // A period in place of the range of days, not inside it.
            params.delete('from')
            params.delete('to')
        })
    }

    const loadMore = () => {
        if (isFetchingNextPage) return
        posthog.capture('my_transactions_page_loaded', {
            page: pageCount + 1,
            has_search: appliedSearch !== '',
            period: period || null,
        })
        fetchNextPage()
    }

    const openRow = (item: IDashboardTransactionResult) => {
        const link = links?.get(item.pk)
        const kind = describeTransaction(item, me, link).kind
        posthog.capture('my_transactions_detail_opened', {
            kind,
            ctx_type: item.ctx_type,
            source: item.source,
            destination: item.destination,
            // Whether the row they opened is one half of a pass-through: the
            // pair is what «−۵,۰۰۰ در کنار +۵,۰۰۰» asks about.
            is_pass_through: !!link,
            // Whether the sheet breaks the amount down into the payment and
            // its commission.
            has_commission: commissionOf(item, kind) > 0,
        })
        setOpenPk(item.pk)
    }

    const isSearching = appliedSearch !== ''
    const hasPeriod = period !== ''
    const hasFilters = filterNames.length > 0
    const periodLabel =
        PERIOD_OPTIONS.find((option) => option.key === period)?.label ?? ''
    const summaryTitle = hasDays ? 'بازهٔ انتخابی' : periodLabel
    const summaryRange = hasDays
        ? dayRangeLabel(filters.from, filters.to)
        : range
        ? periodRangeLabel(range)
        : ''

    // The fund's name as the sheet lists it; off the rows, for a box the
    // member has since left or before their list of boxes is in.
    const chosenBox = filters.box
    const boxTitle = chosenBox
        ? funds.find(
              (fund) =>
                  fund.box.type === chosenBox.type &&
                  fund.box.id === chosenBox.id
          )?.title ?? boxTitleFromRows(rows, chosenBox)
        : null
    const chips = filterChips(filters, boxTitle)

    const emptyText =
        isSearching && hasPeriod
            ? 'تراکنشی با این جستجو در این بازه پیدا نشد.'
            : isSearching
            ? 'تراکنشی در صندوقی با این نام پیدا نشد.'
            : hasPeriod
            ? 'در این بازه تراکنشی ندارید.'
            : ''

    return (
        <>
            <Header title="تراکنش‌ها" />
            <SideBar>
                <SideBarItems />
            </SideBar>

            <div className="min-h-screen bg-white pb-6">
                <div className="flex min-w-0 items-center gap-2 px-4 pt-3">
                    <label className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-[10px] bg-sunken px-3 ring-1 ring-transparent transition-shadow focus-within:ring-green-1">
                        <IoSearch
                            aria-hidden
                            className="h-[18px] w-[18px] shrink-0 text-text-secondary"
                        />
                        <input
                            type="search"
                            enterKeyHint="search"
                            aria-label="جستجوی تراکنش‌ها با نام صندوق"
                            className="min-w-0 flex-1 text-ellipsis bg-transparent text-sm text-black outline-none placeholder:text-[13px] placeholder:text-text-muted [&::-webkit-search-cancel-button]:hidden"
                            placeholder="جستجوی نام صندوق"
                            value={searchText}
                            onChange={(event) =>
                                setSearchText(event.target.value)
                            }
                            onKeyDown={(event) => {
                                if (event.key !== 'Enter') return
                                applySearch(searchText)
                                event.currentTarget.blur()
                            }}
                        />
                        {searchText !== '' && (
                            <button
                                type="button"
                                aria-label="پاک کردن جستجو"
                                onClick={() => {
                                    setSearchText('')
                                    applySearch('')
                                }}
                                className="-ml-1 shrink-0 p-1 text-text-secondary"
                            >
                                <IoClose className="h-4 w-4" />
                            </button>
                        )}
                    </label>
                    <FilterButton
                        count={filterNames.length}
                        onClick={openFilters}
                    />
                </div>

                {/* Always on screen, not behind a filter button: which months
                    are shown is what makes the list read as the whole of it
                    or as a part. */}
                <div
                    role="radiogroup"
                    aria-label="بازهٔ زمانی"
                    className="flex gap-2 overflow-x-auto px-4 pb-0.5 pt-2.5"
                    style={{ scrollbarWidth: 'none' }}
                >
                    {PERIOD_OPTIONS.map((option) => (
                        <SortPill
                            key={option.key || 'all'}
                            // A range of days of their own is none of these.
                            checked={!hasDays && period === option.key}
                            onChange={() => choosePeriod(option.key)}
                            label={option.label}
                            aria={option.aria}
                        />
                    ))}
                </div>

                <FilterChips
                    chips={chips}
                    onClear={(which) => clearFilters(which, 'chip')}
                />

                {/* A gateway payment is two rows, a top-up and the payment,
                    and the top-up belongs to no box: under a fund or a kind of
                    payment only the payment is listed. Said, not left to be
                    noticed. */}
                {topUpsHidden && (
                    <p className="mx-4 mb-0 mt-2 flex items-start gap-1.5 text-[11px] leading-5 text-text-secondary">
                        <IoInformationCircleOutline
                            aria-hidden
                            className="mt-0.5 h-3.5 w-3.5 shrink-0"
                        />
                        <span className="min-w-0">
                            ردیف «شارژ کیف پول» که کنار هر پرداخت با درگاه
                            می‌آید، با این فیلتر نمایش داده نمی‌شود؛ خود پرداخت
                            در فهرست هست.
                        </span>
                    </p>
                )}

                {totals && hasWindow && rows && rows.length > 0 && (
                    <PeriodSummary
                        title={summaryTitle}
                        range={summaryRange}
                        totals={totals}
                        isCounting={isCounting}
                        cappedAt={summaryCapped ? rows.length : null}
                        isPartial={hasWindow && !!hasNextPage && isError}
                        passThroughCount={passThroughCount}
                        topUpsHidden={topUpsHidden}
                    />
                )}

                {isError && !rows ? (
                    <div className="mx-4 mt-5">
                        <SectionError
                            message="تراکنش‌ها دریافت نشد"
                            onRetry={() => refetch()}
                        />
                    </div>
                ) : isLoading || isResolvingMe || !rows ? (
                    <ListSkeleton />
                ) : rows.length === 0 ? (
                    hasFilters ? (
                        <div className="mt-10 flex flex-col items-center px-6 text-center">
                            <p className="mb-0 text-sm leading-6 text-text-secondary">
                                {isSearching
                                    ? 'تراکنشی با این جستجو و این فیلترها پیدا نشد.'
                                    : 'تراکنشی با این فیلترها پیدا نشد.'}
                            </p>
                            <button
                                type="button"
                                onClick={() =>
                                    clearFilters('all', 'empty_state')
                                }
                                className="mt-3 h-9 rounded-[10px] bg-brand-soft px-4 text-[13px] font-bold text-brand-deep"
                            >
                                حذف فیلترها
                            </button>
                        </div>
                    ) : emptyText ? (
                        <p className="mt-10 px-6 text-center text-sm leading-6 text-text-secondary">
                            {emptyText}
                        </p>
                    ) : (
                        <div className="mt-12 flex flex-col items-center px-8 text-center">
                            <span
                                aria-hidden
                                className="flex h-14 w-14 items-center justify-center rounded-full bg-sunken text-text-secondary"
                            >
                                <IoReceiptOutline className="h-7 w-7" />
                            </span>
                            <p className="mb-0 mt-4 text-[15px] font-bold text-black">
                                هنوز تراکنشی ندارید
                            </p>
                            <p className="mb-0 mt-1.5 text-[13px] leading-6 text-text-secondary">
                                پرداخت‌های شما در صندوق‌ها، شارژ کیف پول و
                                ارتقای صندوق اینجا دیده می‌شود.
                            </p>
                            <button
                                type="button"
                                onClick={() => navigate('/funds')}
                                className="mt-4 h-10 rounded-[10px] bg-brand-soft px-5 text-sm font-bold text-brand-deep"
                            >
                                دیدن صندوق‌ها
                            </button>
                        </div>
                    )
                ) : (
                    <InfiniteScroll
                        next={loadMore}
                        hasMore={!!hasNextPage && !isError}
                        dataLength={rows.length}
                        loader={<DotsLoading />}
                        // The library's wrapper is `overflow: auto`, which
                        // would make IT the box the sticky day headings stick
                        // to — and it never scrolls, so they never would.
                        style={{ overflow: 'visible' }}
                    >
                        {days.map((day, index) => {
                            const { title, date: when } = describeDay(day.key)
                            return (
                                <section
                                    key={`${day.key}-${index}`}
                                    aria-label={`${title} ${when}`}
                                >
                                    <h3 className="sticky top-14 z-[5] m-0 flex items-baseline gap-1.5 bg-white px-5 pb-1.5 pt-4 text-xs">
                                        <span className="font-bold text-black">
                                            {title}
                                        </span>
                                        <span className="text-text-secondary">
                                            {when}
                                        </span>
                                    </h3>
                                    <div className="mx-4 divide-y divide-divider overflow-hidden rounded-card border border-[#E4E7EB] bg-white">
                                        {day.rows.map((item) => (
                                            <Item
                                                key={item.pk}
                                                item={item}
                                                view={describeTransaction(
                                                    item,
                                                    me,
                                                    links?.get(item.pk)
                                                )}
                                                onOpen={() => openRow(item)}
                                            />
                                        ))}
                                    </div>
                                </section>
                            )
                        })}
                    </InfiniteScroll>
                )}

                {/* A page after the first that failed: the rows already
                    shown stay, and the rest can be asked for again. */}
                {isError && rows && rows.length > 0 && (
                    <div className="mx-4 mt-4 flex items-center justify-between gap-2 rounded-[10px] bg-sunken px-3 py-2.5 text-[13px]">
                        <p className="mb-0 text-text-secondary">
                            ادامهٔ تراکنش‌ها دریافت نشد
                        </p>
                        <button
                            type="button"
                            onClick={() =>
                                hasNextPage ? fetchNextPage() : refetch()
                            }
                            className="shrink-0 font-bold text-green-1"
                        >
                            تلاش مجدد
                        </button>
                    </div>
                )}
            </div>

            <CustomBottomSheet
                onDismiss={() => setOpenPk(null)}
                isOpen={!!openItem}
            >
                {openItem && (
                    <TransactionDetails
                        key={openItem.pk}
                        item={openItem}
                        view={describeTransaction(
                            openItem,
                            me,
                            links?.get(openItem.pk)
                        )}
                        me={me}
                        link={links?.get(openItem.pk)}
                        topUpsHidden={topUpsHidden}
                        onClose={() => setOpenPk(null)}
                    />
                )}
            </CustomBottomSheet>

            <FilterSheet
                isOpen={filterOpen}
                filters={filters}
                funds={funds}
                fundsLoading={boxes.isLoading}
                fundsError={boxes.isError}
                onRetryFunds={() => boxes.refetch()}
                boxTitle={boxTitle}
                onApply={applyFilters}
                onClear={() => {
                    setFilterOpen(false)
                    if (filterNames.length > 0) clearFilters('all', 'sheet')
                }}
                onDismiss={() => setFilterOpen(false)}
            />
        </>
    )
}
